import { useMemo, useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { prepareLogsDateRange } from 'infrastructure/functions/logs';
import { ThresholdLog } from 'infrastructure/classes/logs/threshold-log';

import useApi from '../use-api';

import type { DateRange } from 'infrastructure/types';
import type {
  IPaginationMeta,
  IPartialPaginationMeta,
  IPaginatedThresholdLogs,
} from 'infrastructure/interfaces';
import type { Dispatch, SetStateAction } from 'react';

const thresholdLogApi = (patientGuid: string) =>
  `core/patients/${patientGuid}/threshold-logs`;

interface IUseThresholdLogMeta {
  dateRange?: DateRange;
}

type mergedMeta = IUseThresholdLogMeta & IPaginationMeta;

type UseLog = {
  loading: boolean;
  items: Array<ThresholdLog>;
  meta: mergedMeta;
  setMeta: Dispatch<SetStateAction<mergedMeta>>;
};

interface IUseLogProps {
  patientGuid: string;
  meta?: IUseThresholdLogMeta & IPartialPaginationMeta;
}

export const useThresholdLog = (props: IUseLogProps): UseLog => {
  const options = props;
  const { patientGuid, meta: metaProps = {} } = options;
  const dateRangeFromProps = metaProps.dateRange;

  const [meta, setMeta] = useState<mergedMeta>({
    page: 1,
    items: 15,
    totalCount: 0,
    ...metaProps,
  });

  const { loadData } = useApi();

  const startTime = dateRangeFromProps?.[0]?.getTime() ?? null;
  const endTime = dateRangeFromProps?.[1]?.getTime() ?? null;

  useEffect(() => {
    setMeta((currentMeta) => {
      if (
        (currentMeta.dateRange?.[0]?.getTime() ?? null) === startTime &&
        (currentMeta.dateRange?.[1]?.getTime() ?? null) === endTime
      ) {
        return currentMeta;
      }
      return { ...currentMeta, page: 1, dateRange: dateRangeFromProps };
    });
  }, [startTime, endTime]);

  const preparedDateRange = useMemo(
    () => prepareLogsDateRange(meta.dateRange),
    [meta.dateRange],
  );

  const queryKey = [
    thresholdLogApi(patientGuid),
    preparedDateRange.startTime,
    preparedDateRange.endTime,
    meta.page,
    meta.items,
  ];

  const { data = [], isLoading } = useQuery({
    queryKey,
    queryFn: async () =>
      loadData<IPaginatedThresholdLogs>(thresholdLogApi(patientGuid), {
        page: meta.page,
        items: meta.items,
        ...preparedDateRange,
      }).then((res) => {
        if (res) {
          const { count, data: rows } = res;

          setMeta((currentMeta) => ({
            ...currentMeta,
            totalCount: count,
          }));

          return rows.map((el) => new ThresholdLog(el));
        }
      }),
    enabled: Boolean(patientGuid),
  });

  return {
    loading: isLoading,
    items: data,
    meta,
    setMeta,
  };
};
